"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { Sparkles } from "lucide-react";
import MarketplaceVerticalSnapFeed from "@/components/feed/marketplace-vertical-snap-feed";
import { type ListingForVerticalCard } from "@/components/feed/marketplace-vertical-listing-card";
import type { FeedFilterState } from "@/hooks/use-paginated-feed";
import { usePaginatedFeed } from "@/hooks/use-paginated-feed";
import MarketplaceVerticalSnapFeedSkeleton from "@/components/feed/marketplace-vertical-snap-feed-skeleton";
import { FEED_SNAP_INDEX_KEY_FOR_YOU } from "@/lib/feed-snap-index";

const FOR_YOU_LAST_REFRESH_KEY = "feed-for-you-last-refresh";
const FOR_YOU_REFRESH_MIN_MS = 5 * 60 * 1000;

type SortOption = "recommended" | "price-asc" | "price-desc" | "date-asc" | "date-desc" | null;

interface RecommendedFeedProps {
  filters: FeedFilterState & { sortBy: SortOption };
  currentUserId?: string;
  isActive?: boolean;
  onMobileSheetChangeAction?: (open: boolean) => void;
}

export default function RecommendedFeed({
  filters,
  currentUserId,
  isActive = true,
  onMobileSheetChangeAction = () => {},
}: RecommendedFeedProps) {
  const { sortBy, ...criteria } = filters;
  const searchParams = useSearchParams();
  const debug = searchParams.get("debug") === "true";

  const {
    items,
    isLoading,
    isInitialized,
    error,
    hasNextPage,
    loadMore,
    refresh,
    refetchFirstPage,
    reroll,
  } = usePaginatedFeed({
    userId: currentUserId,
    mode: "recommended",
    filters: criteria,
    limit: 12,
    debug,
    // Persist + restore across navigation (skeleton-on-back fix). nextCursor
    // (mode "recommended" pagination) is already part of the persisted
    // snapshot shape — see lib/feed/feed-snapshot.ts.
    cacheKey: "for-you",
  });

  const processedItems = useMemo(() => {
    if (!sortBy || sortBy === "recommended") return items;
    const list = [...items];
    if (sortBy === "price-asc") {
      list.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === "price-desc") {
      list.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === "date-asc") {
      list.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortBy === "date-desc") {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    return list;
  }, [items, sortBy]);

  const isInitialLoading = !isInitialized && isLoading;
  const isLoadingMore = isInitialized && isLoading;
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreArmedRef = useRef(true);
  // Initializing to the CURRENT `isActive` (not `false`) is what makes this
  // safe on a fresh/restored mount: the parent only mounts RecommendedFeed
  // while its tab is active, so `isActive` is already `true` on the very
  // first render (including a back-nav remount with the "for-you" tab
  // restored via feed-page-client-state's readInitialActiveTab). `prev` below
  // then equals `isActive` on the first effect run, so the `!prev && isActive`
  // reroll only fires on a genuine false->true transition (switching INTO
  // this tab from another one) — never on mount.
  const prevActiveRef = useRef(isActive);

  const refreshForYou = useCallback(() => {
    try {
      sessionStorage.setItem(FEED_SNAP_INDEX_KEY_FOR_YOU, "0");
    } catch {
      /* private mode */
    }
    reroll();
  }, [reroll]);

  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = isActive;
    if (!prev && isActive && isInitialized) {
      refreshForYou();
    }
  }, [isActive, isInitialized, refreshForYou]);

  useEffect(() => {
    if (!isActive) return;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      try {
        const last = Number(sessionStorage.getItem(FOR_YOU_LAST_REFRESH_KEY) || "0");
        if (Date.now() - last < FOR_YOU_REFRESH_MIN_MS) return;
        sessionStorage.setItem(FOR_YOU_LAST_REFRESH_KEY, String(Date.now()));
      } catch {
        /* private mode */
      }
      refreshForYou();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [isActive, refreshForYou]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!node || !root || !hasNextPage || isLoadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          loadMoreArmedRef.current = true;
          return;
        }
        if (!loadMoreArmedRef.current) return;
        loadMoreArmedRef.current = false;
        loadMore();
      },
      { root, rootMargin: "600px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isLoadingMore, loadMore]);

  useEffect(() => {
    loadMoreArmedRef.current = true;
  }, [processedItems.length]);

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center space-y-8 max-w-md px-4">
          <Sparkles className="w-20 h-20 text-foreground opacity-90 mx-auto" strokeWidth={1.5} />
          <div className="space-y-3">
            <h1 className="text-4xl tracking-tight text-foreground">Oops!</h1>
            <p className="text-lg text-muted-foreground tracking-wide">We couldn’t load recommendations</p>
          </div>
          <button
            onClick={refresh}
            className="px-8 py-3 border border-foreground text-foreground tracking-wide hover:bg-foreground hover:text-background transition-all duration-300 ease-in-out rounded-full"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full min-h-0 flex flex-col">
      {isInitialLoading ? <MarketplaceVerticalSnapFeedSkeleton /> : null}

      {!error && processedItems.length > 0 ? (
        <div className="flex-1 min-h-0 flex flex-col">
        <MarketplaceVerticalSnapFeed
          onScrollContainerRef={(el) => {
            scrollContainerRef.current = el;
          }}
          loadMoreSentinelRef={sentinelRef}
          listings={processedItems.map((listing) => ({
            id: listing.id,
            title: listing.title,
            description: listing.description,
            price: listing.price,
            currency: listing.currency ?? null,
            image_urls: listing.image_urls,
            shipFromAddress: listing.shipFromAddress ?? null,
            condition: listing.condition,
            grade: listing.grade,
            created_at: listing.created_at,
            user_id: listing.user_id,
            quantity: listing.quantity,
            sellerUsername: listing.sellerUsername,
            sellerAvatarUrl: listing.sellerAvatarUrl,
            sellerIsFollowing: listing.sellerIsFollowing,
            initialSaved: listing.initialSaved,
            scheduled_publish_at: listing.scheduled_publish_at ?? null,
            listing_format: listing.listing_format ?? null,
            auction_start_price: listing.auction_start_price ?? null,
            auction_highest_bid: listing.auction_highest_bid ?? null,
            auction_end_at: listing.auction_end_at ?? null,
            auction_status: listing.auction_status ?? null,
            auction_bid_count: listing.auction_bid_count ?? 0,
            type: (listing as { type?: "standard" | "preorder" | null }).type ?? null,
            sellerSalesCount: listing.sellerSalesCount,
            card_details: listing.card_details ?? null,
            // Phase 3 (2026-06-29): `accepts_offers` collapsed — derive via
            // `isOfferEnabledFormat(listing_format)`.
            offers_pending_count:
              (listing as { offers_pending_count?: number | null }).offers_pending_count ?? 0,
            // Pass the API-computed saved count straight through so the card
            // seeds it (no per-card Supabase count query). Undefined stays
            // undefined → card shows no count (RPC-soft-fail degrade contract).
            saves_count: (listing as { saves_count?: number }).saves_count,
          } satisfies ListingForVerticalCard))}
          currentUserId={currentUserId}
          hasNextPage={hasNextPage}
          isLoadingMore={isLoadingMore}
          onLoadMoreAction={loadMore}
          onFollowChangeAction={refetchFirstPage}
          onToggleSavedAction={refetchFirstPage}
          onMobileSheetChangeAction={onMobileSheetChangeAction}
          snapIndexStorageKey={FEED_SNAP_INDEX_KEY_FOR_YOU}
        />
        </div>
      ) : null}

      {isInitialized && processedItems.length === 0 && !error && (
        <div className="flex items-center justify-center min-h-[60vh]">
          <p className="text-muted-foreground dark:text-white/50 text-center">
            No recommendations yet. Listings you save or interact with will shape your feed.
          </p>
        </div>
      )}
    </div>
  );
}
